<template>
  <div class="flex flex-col h-full overflow-hidden">
    <ThreadsNav show-back :show-center="false">
      <template #left>
        <div
          v-if="memory.id"
          class="flex gap-1 text-xs md:text-sm items-center rounded-full border px-2 whitespace-nowrap py-1"
        >
          <div
            :class="[
              'rounded-full w-2 h-2',
              savingError ? 'bg-error' : isSaving ? 'bg-warning' : 'bg-success',
            ]"
          />
          <span>
            {{
              savingError
                ? t(`Message.savingFailed`)
                : t(`User.settings${isSaving ? "Unsaved" : "Saved"}`)
            }}
          </span>
        </div>
      </template>
      <template #right>
        <CrystalPowerCount />
      </template>
    </ThreadsNav>

    <div class="flex flex-col gap-4 overflow-auto h-full">
      <div>
        <div class="red-dot ml-5 text-base mb-2">
          {{ t(`memory.type.${memory.type}`) }}记忆球基本信息
        </div>

        <VanCellGroup inset>
          <VanField
            required
            label="名称"
            placeholder="请输入名称"
            v-model="memory.name"
            @blur="handleSave('name')"
          />

          <VanField
            required
            label="容量"
            placeholder="请选择容量"
            :disabled="!!memory.id"
            :model-value="
              memory.capacityLevel && memory.type
                ? $t(
                    `memory.capacityLevel.${memory.type}.${memory.capacityLevel}`
                  )
                : ''
            "
            :is-link="!memory.id"
            readonly
            @click="!memory.id && (showCapacityLevelPicker = true)"
          />

          <VanCell
            title="颜色"
            is-link
            title-class="flex-none w-[var(--van-field-label-width)] ml-2"
            value-class="text-left relative overflow-visible"
            @click="clickShowColorPicker"
          >
            <template #value>
              <div class="ml-1">
                <input
                  type="color"
                  v-model="memory.labelColor"
                  ref="colorPickerRef"
                  @input="handleColorPickerInput"
                />
              </div>
            </template>
          </VanCell>
        </VanCellGroup>
      </div>

      <div>
        <div
          class="red-dot mx-5 mb-2 text-base flex justify-between items-center"
        >
          <div>记忆内容</div>
          <div
            :class="[
              'text-sm text-base-content/50',
              (memory.text?.length ?? 0) >=
              (MemoryCapacityLevelMap[memory.type]?.[memory.capacityLevel] ?? 0)
                ? 'text-error'
                : '',
            ]"
          >
            {{
              (memory.text?.length ?? 0) +
              "/" +
              (MemoryCapacityLevelMap[memory.type]?.[memory.capacityLevel] ?? 0)
            }}
          </div>
        </div>

        <div class="mx-5">
          <div class="text-sm text-base-content/50 whitespace-pre-wrap">
            {{ memory.text }}
          </div>
          <button
            :disabled="isAutoExtracted"
            class="btn w-full btn-sm mt-2"
            @click="handleEditMemory"
          >
            编辑记忆内容
          </button>
        </div>
      </div>
      <div>
        <div
          class="red-dot mx-5 mb-2 text-base flex justify-between items-center"
        >
          <div>自动提取记忆</div>
          <VanSwitch
            :model-value="isAutoExtracted"
            size="14"
            @change="handleAutoExtractedChange"
          />
        </div>

        <div class="mx-5 flex gap-2 flex-col" v-if="isAutoExtracted">
          <div class="text-base-content/50" v-if="totalCount">
            已选取来源 ({{ totalCount }})
          </div>
          <van-checkbox
            v-if="memory.type === MemoryType.Core"
            v-model="source.profile"
            shape="square"
          >
            OC 设定卡片
          </van-checkbox>

          <div>{{ statisticText }}</div>

          <SelectBackground
            :mate-id="memory.mateId"
            :title="
              memory.type === MemoryType.Core ? '选择更多来源' : '选择来源'
            "
            btn-class="btn-sm w-full mt-1"
            v-model:journalzz="source.journalzz"
            v-model:mailzz="source.mailzz"
            v-model:dispatchzz="source.dispatchzz"
            v-model:momentzz="source.momentzz"
            v-model:roleplayAnswerzz="source.roleplayAnswerzz"
            :type-list="[
              'journal',
              'mail',
              'dispatch',
              'moment',
              'roleplayAnswer',
            ]"
            is-vertical
          />
        </div>

        <div class="m-5 flex flex-col gap-2">
          <div class="flex gap-2 items-center w-full" v-if="isAutoExtracted">
            <button
              class="btn btn-sm btn-primary flex-1"
              @click="handleSaveOrCreateMemory"
              v-if="powerCount > 0"
            >
              {{ memory.id ? "重新提取" : "创建" }}

              <template v-if="crystalCount && !memory.id">
                <CrystalIcon /> {{ crystalCount }}
              </template>

              <template v-if="!userStore.isFreeModel()">
                <PowerIcon /> {{ userStore.getPowerCostByModel(powerCount) }}
              </template>
            </button>

            <SelectModelBtn class="btn-sm" v-if="powerCount > 0" />
          </div>
          <button
            v-else-if="!memory.id"
            class="btn w-full btn-sm btn-primary"
            @click="handleSaveOrCreateMemory"
          >
            保存
            <template v-if="crystalCount">
              <CrystalIcon /> {{ crystalCount }}
            </template>
          </button>
        </div>
      </div>
    </div>

    <ModalSelectOption
      v-model:show="showCapacityLevelPicker"
      :options="capacityLevelOptions"
      @confirm="handleCapacityLevelConfirm"
    />
  </div>
</template>

<script setup lang="ts">
import { createOneMateMemory, updateOneMateMemory } from "@/apis/mate";
import { fixNumber } from "@/components/Mission/MissionStat";
import {
  MemoryCapacityLevel,
  MemoryCapacityLevelCrystalMap,
  MemoryCapacityLevelMap,
  MemoryType,
} from "@/enums/mateEnums";
import { useGlobalModal } from "@/stores/globalModal";
import { useGlobalUser } from "@/stores/globalUser";
import { sessionStorageGet } from "@/tools/sessionStorage";
import {
  MateDispatch,
  MateJournal,
  MateMail,
  MateMemory,
  MateMemoryExtractionSource,
  MateRoleplayAnswer,
  Moment,
} from "@/types/entity";
import { routerBack } from "@/use/go";
import { useDebounceFn } from "@vueuse/core";
import { showConfirmDialog, showLoadingToast, showSuccessToast } from "vant";
import { ToastWrapperInstance } from "vant/es";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

const memory = ref<MateMemory>({
  capacityLevel: MemoryCapacityLevel.Small,
  labelColor: "#ff0000",
  isAutoExtracted: false,
} as MateMemory);
const { t } = useI18n();
const route = useRoute();

const modalStore = useGlobalModal();
const userStore = useGlobalUser();
const isAutoExtracted = ref(false);
const isSaving = ref(false);
const savingError = ref(false);

const source = ref({
  profile: false,
  journalzz: [] as MateJournal[],
  mailzz: [] as MateMail[],
  dispatchzz: [] as MateDispatch[],
  momentzz: [] as Moment[],
  roleplayAnswerzz: [] as MateRoleplayAnswer[],
});

const capacityLevelOptions = computed(() => [
  {
    text: t(
      `memory.capacityLevel.${memory.value.type}.${MemoryCapacityLevel.Small}`
    ),
    value: MemoryCapacityLevel.Small,
  },
  {
    text: t(
      `memory.capacityLevel.${memory.value.type}.${MemoryCapacityLevel.Medium}`
    ),
    value: MemoryCapacityLevel.Medium,
  },
  {
    text: t(
      `memory.capacityLevel.${memory.value.type}.${MemoryCapacityLevel.Large}`
    ),
    value: MemoryCapacityLevel.Large,
  },
]);

const statisticText = computed(() => {
  return [
    { text: "信件", count: source.value.mailzz?.length },
    { text: "随记", count: source.value.momentzz?.length },
    {
      text: "手账",
      count: source.value.journalzz?.length,
    },
    { text: "任务", count: source.value.dispatchzz?.length },
    { text: "问答", count: source.value.roleplayAnswerzz?.length },
  ]
    .filter((item) => item.count)
    .map((item) => `${item.text}(${item.count})`)
    .join("，");
});

const totalCount = computed(() => {
  return (
    (source.value.mailzz?.length ?? 0) +
    (source.value.momentzz?.length ?? 0) +
    (source.value.roleplayAnswerzz?.length ?? 0) +
    (source.value.journalzz?.length ?? 0) +
    (source.value.dispatchzz?.length ?? 0) +
    (source.value.profile ? 1 : 0)
  );
});

const crystalCount = computed(() => {
  return (
    MemoryCapacityLevelCrystalMap[memory.value.type]?.[
      memory.value.capacityLevel
    ] ?? 0
  );
});

const powerCount = computed(() => {
  let total = totalCount.value;
  let cost = 0;

  if (source.value.profile) {
    cost += 5;
    total -= 1;
  }

  cost += total * 0.5;
  return fixNumber(cost);
});

const showCapacityLevelPicker = ref(false);

const showColorPicker = ref(false);

const colorPickerRef = ref<HTMLInputElement>();

// 预定义的好看颜色选项
const predefinedColors = [
  "#FF6B6B", // 红色
  "#4ECDC4", // 青色
  "#45B7D1", // 蓝色
  "#96CEB4", // 绿色
  "#FFEAA7", // 黄色
  "#DDA0DD", // 紫色
  "#FFB347", // 橙色
  "#87CEEB", // 天蓝色
  "#98FB98", // 浅绿色
  "#F0E68C", // 卡其色
  "#DDA0DD", // 梅红色
  "#B0E0E6", // 粉蓝色
];

// 生成随机颜色的函数
const generateRandomColor = (): string => {
  return predefinedColors[Math.floor(Math.random() * predefinedColors.length)];
};

const clickShowColorPicker = () => {
  showColorPicker.value = true;
  colorPickerRef.value?.click();
};

const handleCapacityLevelConfirm = (value: MemoryCapacityLevel) => {
  memory.value.capacityLevel = value;
  showCapacityLevelPicker.value = false;
};

const debouncedColorSave = useDebounceFn(() => handleSave("labelColor"), 1000);

const handleColorPickerInput = (e: Event) => {
  const input = e.target as HTMLInputElement;
  memory.value.labelColor = input.value;

  if (memory.value.id) {
    isSaving.value = true;
    debouncedColorSave();
  }
};

const handleSave = async (key: keyof MateMemory) => {
  if (!memory.value.id) return;
  isSaving.value = true;
  savingError.value = false;
  const params: Parameters<typeof updateOneMateMemory>[1] = {
    [key]: memory.value[key],
  };

  if (key === "isAutoExtracted" && memory.value.isAutoExtracted) {
    params.extractionSource = memory.value.extractionSource;
  }

  try {
    await updateOneMateMemory(memory.value.id, params);
    isSaving.value = false;
  } catch {
    savingError.value = true;
  }
};

const handleEditMemory = () => {
  modalStore.openRichEditor({
    title: "编辑记忆内容",
    content: memory.value.text,
    maxLength:
      MemoryCapacityLevelMap[memory.value.type]?.[memory.value.capacityLevel] ??
      0,
    callback: (value?: string) => {
      if (value) {
        memory.value.text = value;
        handleSave("text");
      }
    },
    type: "longText",
  });
};

const handleAutoExtractedChange = async (value: boolean) => {
  if (value) {
    memory.value.extractionSource = {
      profile: source.value.profile,
      mailIds: [] as number[],
      momentIds: [] as number[],
      roleplayAnswerIds: [] as number[],
      dispatchIds: [] as number[],
      journalIds: [] as number[],
    };
  }

  if (value && memory.value.text) {
    try {
      await showConfirmDialog({
        title: "提示",
        message: "开启后，将会清空现有的记忆内容，是否继续？",
      });
      memory.value.text = "";
    } catch {
      return;
    }
  }
  memory.value.isAutoExtracted = value;
  isAutoExtracted.value = value;
};

const handleSaveOrCreateMemory = async () => {
  let toast: ToastWrapperInstance | undefined = undefined;

  let extractionSource: MateMemoryExtractionSource = {
    profile: source.value.profile,
  };

  if (isAutoExtracted.value) {
    extractionSource.journalIds = source.value.journalzz.map((v) => v.id);
    extractionSource.mailIds = source.value.mailzz.map((v) => v.id);
    extractionSource.momentIds = source.value.momentzz.map((v) => v.id);
    extractionSource.roleplayAnswerIds = source.value.roleplayAnswerzz.map(
      (v) => v.id
    );
    extractionSource.dispatchIds = source.value.dispatchzz.map((v) => v.id);

    if (
      modalStore.shouldOpenPowerNotEnoughModal({
        spentPowerAmount: powerCount.value,
        cb: () => handleSaveOrCreateMemory(),
        plusModelRatio: true,
      })
    )
      return;
  }

  try {
    if (memory.value.id) {
      // 重新提取
      toast = showLoadingToast(t(`Message.saving`));
      await updateOneMateMemory(memory.value.id, {
        extractionSource,
        isAutoExtracted: true,
      });
      userStore.refresh();
    } else {
      toast = showLoadingToast(`创建中...`);
      await createOneMateMemory({
        ...memory.value,
        extractionSource,
      });

      if (memory.value.isAutoExtracted) {
        userStore.refresh();
      }
    }
    toast?.close();
    showSuccessToast({
      message: t(`Message.opSuccess`),
      forbidClick: true,
      onClose() {
        routerBack();
      },
    });
  } catch (error) {
    toast?.close();
  }
};

onMounted(() => {
  const type = route.query.type as unknown as MemoryType;
  const memoryId = route.query.memoryId as string;
  const mateId = route.query.mateId as string;

  if (type) {
    memory.value.type = Number(type);
    memory.value.mateId = Number(mateId);
    // 使用改进的随机颜色生成函数
    memory.value.labelColor = generateRandomColor();
    if (route.query.hasSource) {
      const _source = sessionStorageGet("memory:edit:source");
      if (_source) {
        isAutoExtracted.value = true;
        memory.value.isAutoExtracted = true;
        source.value = {
          ...source.value,
          ..._source,
        };
      }
    }
  } else if (memoryId) {
    const _memory = sessionStorageGet("memory:edit:" + memoryId);
    if (_memory) {
      memory.value = _memory;
    }
  }
});
</script>
<style scoped>
input[type="color"] {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  background: radial-gradient(
    circle at 30% 30%,
    rgba(255, 255, 255, 0.4) 0%,
    transparent 50%
  );
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.3),
    inset 0 -2px 4px rgba(255, 255, 255, 0.4), 0 4px 8px rgba(0, 0, 0, 0.4),
    0 2px 4px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  position: relative;
  overflow: visible;
}

input[type="color"]::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background: radial-gradient(
    circle at 70% 70%,
    rgba(0, 0, 0, 0.2) 0%,
    transparent 50%
  );
  pointer-events: none;
  z-index: 1;
}

input[type="color"]::-webkit-color-swatch-wrapper {
  padding: 0;
  border-radius: 50%;
  overflow: hidden;
}

input[type="color"]::-webkit-color-swatch {
  border: none;
  border-radius: 50%;
  width: 100%;
  height: 100%;
}
</style>
